import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { SpoolCard } from '../../SpoolsProduction';
import { Notification } from '../../../../../../../components/ui/custom-notification';
import { Button } from '../../../../../../../components/ui/button';
import { getSpoolFat, saveSpoolFat } from '../../spoolFatApi';
import * as spoolsbaseActions from '../../../../../../../redux/Spoolsbase/actions';
import type { CoatingSpoolPersistPayload } from '../../../specifications/Coating/CoatingSpoolChecklistEditor';
import type { FatSpecificationTemplateDto } from '../../../specifications/Fat/fatSpecificationTemplateTypes';
import { findFatSpecificationTemplateForSpool } from '../../../specifications/Fat/fatSpecificationTemplateResolve';
import { FAT_CHECKLIST_SPEC_CATEGORY } from '../../../specifications/Fat/fatSpecificationConstants';
import { ProductionChecklistCards } from './ProductionChecklistCards';

function hasChecklistSections(template: FatSpecificationTemplateDto): boolean {
  const payload = template.payload;
  return Boolean(
    payload &&
      typeof payload === 'object' &&
      Array.isArray((payload as Partial<CoatingSpoolPersistPayload>).checklistSections) &&
      (payload as Partial<CoatingSpoolPersistPayload>).checklistSections!.length > 0,
  );
}

function isChecklistPayload(payload: unknown): payload is Partial<CoatingSpoolPersistPayload> {
  return Boolean(payload && typeof payload === 'object' && !Array.isArray(payload));
}

function normalizeChecklistValues(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    out[key] = value == null ? '' : String(value);
  }
  return out;
}

function buildFatProductionDraft(
  specPayload: unknown,
  productionPayload: unknown,
): CoatingSpoolPersistPayload {
  const spec = isChecklistPayload(specPayload) ? specPayload : {};
  const production = isChecklistPayload(productionPayload) ? productionPayload : {};
  const specSections = Array.isArray(spec.checklistSections) ? spec.checklistSections : [];
  const productionSections = Array.isArray(production.checklistSections) ? production.checklistSections : [];

  return {
    checklistSections: specSections.length > 0 ? specSections : productionSections,
    checklistValues: {
      ...normalizeChecklistValues(spec.checklistValues),
      ...normalizeChecklistValues(production.checklistValues),
    },
  };
}

export function FATPanel({
  spool,
  projectId,
  pipeId,
  templates = [],
}: {
  spool: SpoolCard;
  projectId?: string | number | null;
  pipeId?: string | number | null;
  /** FAT specification templates (same GET as Fabrication → Spools). Used to scope steps in production only. */
  templates?: FatSpecificationTemplateDto[];
}) {
  const dispatch = useDispatch();
  const [rowId, setRowId] = useState<number | null>(null);
  const [savedPayload, setSavedPayload] = useState<object | null>(null);
  const [checklistValues, setChecklistValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const scopeOk =
    projectId != null && projectId !== '' && pipeId != null && pipeId !== '' && spool.id != null;

  const fatTemplate = useMemo(() => {
    const fatTemplates = templates.filter((template) => template.category === FAT_CHECKLIST_SPEC_CATEGORY);
    return (
      findFatSpecificationTemplateForSpool(fatTemplates, spool.id, spool.name) ??
      fatTemplates.find(hasChecklistSections) ??
      null
    );
  }, [templates, spool.id, spool.name]);

  const initialDraft = useMemo(
    () => buildFatProductionDraft(fatTemplate?.payload ?? null, savedPayload),
    [fatTemplate?.payload, savedPayload],
  );

  const hasTemplateCategories = initialDraft.checklistSections.length > 0;

  useEffect(() => {
    setChecklistValues(initialDraft.checklistValues);
  }, [initialDraft]);

  const refreshList = useCallback(() => {
    if (!scopeOk) return;
    dispatch(
      spoolsbaseActions.fetchAllSpools({ projectId: String(projectId), pipeId: String(pipeId) })
    );
  }, [dispatch, scopeOk, projectId, pipeId]);

  const load = useCallback(async () => {
    if (!scopeOk) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await getSpoolFat({ projectId, pipeId, spoolId: spool.id });
      const f = res.fat;
      setRowId(f?.id ?? null);
      setSavedPayload(f?.payload && typeof f.payload === 'object' ? f.payload : null);
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load FAT checklist');
      setRowId(null);
      setSavedPayload(null);
    } finally {
      setLoading(false);
    }
  }, [scopeOk, projectId, pipeId, spool.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateChecklistValue = useCallback((key: string, value: string) => {
    setChecklistValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onSave = async () => {
    if (!scopeOk || !hasTemplateCategories) return;
    setSaving(true);
    try {
      const nextPayload = {
        checklistSections: initialDraft.checklistSections,
        checklistValues,
        ...(fatTemplate?.id != null ? { fatSpecificationTemplateId: fatTemplate.id } : {}),
      };
      const res = await saveSpoolFat({
        projectId,
        pipeId,
        spoolId: spool.id,
        submissionMode: 'draft',
        payload: nextPayload,
      });
      setRowId(res.id);
      setSavedPayload(res.payload && typeof res.payload === 'object' ? res.payload : nextPayload);
      Notification.success('FAT', 'Checklist saved.');
      refreshList();
    } catch (e: unknown) {
      Notification.error('FAT', e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!scopeOk) {
    return (
      <p className="text-sm text-amber-800 p-4 rounded border border-amber-200 bg-amber-50/80">
        Open FAT from a pipe context so project and pipe are available, or check spool is linked to a pipe.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {loadError ? <p className="text-sm text-red-600">{loadError}</p> : null}
      {loading ? <p className="text-sm text-gray-500">Loading FAT checklist…</p> : null}
      {rowId != null ? <p className="text-xs text-gray-500">Checklist id {rowId}</p> : null}

      {!hasTemplateCategories ? (
        <p className="rounded-md border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-950">
          No FAT checklist categories are configured for this spool in{' '}
          <span className="font-semibold">Specifications → Fabrication</span>. Add and save FAT
          categories there first; production will show the same criteria-based checklist here.
        </p>
      ) : (
        <div className="space-y-4">
          <h3 className="m-0 text-sm font-semibold text-slate-900">FAT checklist — {spool.name}</h3>
          <ProductionChecklistCards
            sections={initialDraft.checklistSections}
            values={checklistValues}
            onValueChange={updateChecklistValue}
          />
          <div className="flex justify-end">
            <Button
              type="button"
              className="bg-hero-red text-white hover:bg-hero-red-60"
              disabled={saving}
              onClick={() => void onSave()}
            >
              {saving ? 'Saving…' : 'Save FAT checklist'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
